'use client';
import { motion, MotionValue, useScroll, useTransform } from 'motion/react';
import React, { useEffect, useState } from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import { VideoMedia } from '@/components/VideoMedia';
import cssBreakpoints from '@/cssBreakpoints';
import type { Hero, Media } from '@/payload-types';
import { cn } from '@/utils/cn';

import { HeroContent } from '../HeroContent';
import styles from './ParallaxHero.module.scss';

type Props = Hero & {
  locale?: string;
};

function useParallax(value: MotionValue<number>, distance: number) {
  return useTransform(value, [0, 1], [-distance, distance], {
    ease: (v) => 1 - Math.pow(1 - v, 2),
  });
}

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } },
};

export const ParallaxHero: React.FC<Props> = (props) => {
  const {
    alignment,
    backgroundColor,
    image,
    mediaType,
    svgIllustration,
    videoEmbedUrl,
    videoIsBackground,
    videoLoops,
    videoPoster,
  } = props;

  const { scrollYProgress } = useScroll();
  const [clientWidth, setClientWidth] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setClientWidth(window.innerWidth);
    }
  }, []);

  const parallaxStart =
    clientWidth && clientWidth < cssBreakpoints.m ? -80 : -256;

  const y = useParallax(scrollYProgress, -parallaxStart);

  const MediaComponent =
    mediaType === 'image' && image ? (
      <ImageMedia
        aspectRatio={
          clientWidth && clientWidth < cssBreakpoints.m
            ? 'portrait'
            : 'standard'
        }
        priority
        image={image}
        responsiveWidths={
          clientWidth && clientWidth < cssBreakpoints.m
            ? {
                mobile: 343,
                desktop: 512,
                largeDesktop: 800,
              }
            : {
                mobile: 343,
                desktop: 1352,
                largeDesktop: 2256,
              }
        }
      />
    ) : mediaType === 'video' && videoEmbedUrl ? (
      <VideoMedia
        aspectRatio={
          clientWidth && clientWidth < cssBreakpoints.m
            ? 'portrait'
            : 'standard'
        }
        videoPoster={videoPoster as Media}
        videoEmbedUrl={videoEmbedUrl}
        videoIsBackground={videoIsBackground as boolean}
        videoLoops={videoLoops as boolean}
      />
    ) : null;

  return (
    <div
      className={cn(styles.wrapper)}
      style={{
        paddingBottom: clientWidth && clientWidth < cssBreakpoints.m ? 40 : 40,
      }}
      data-hero
      data-background-color={backgroundColor || 'white'}
    >
      <div
        className={cn(
          styles.container,
          backgroundColor ? `${styles[`wrapper--${backgroundColor}`]}` : ''
        )}
        style={{
          maskImage: `-webkit-linear-gradient(top, rgba(0,0,0,1),rgba(0,0,0,1)),
                      url(${(svgIllustration as Media)?.url})`,
        }}
      >
        <div className='container'>
          <HeroContent {...props} />
        </div>
      </div>
      <motion.div
        className={cn(styles.media, alignment && styles[alignment])}
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ y, willChange: 'opacity, transform' }}
      >
        {MediaComponent}
      </motion.div>
    </div>
  );
};
